<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuth } from "@/composables/useAuth";
import api from "@/api/axios";
import SidenavItem from "./SidenavItem.vue";

const openMenu = ref(null);
const { user, hasPermission } = useAuth();
const tieneModulo = (modulo) => user.value?.modules?.includes(modulo) ?? true;
// Analítica (Reportes/Contabilidad) es visible para admin, o para cualquiera
// con el permiso puntual concedido desde Gestión de Usuarios > Permisos.
const veAnalitica = () => user.value?.role === "admin" || hasPermission("analitica.view");

// Visibilidad de menú por ítem: admin/platform_admin siempre ven todo (mismo
// bypass que el backend); cualquier otro rol necesita el permiso puntual
// concedido desde Gestión de Usuarios > Permisos (ver migración 026).
const puedeVer = (permiso) => {
  const rol = user.value?.role;
  if (rol === "admin" || rol === "platform_admin") return true;
  return hasPermission(permiso);
};

const veVentas = () =>
  puedeVer("ventas.nueva_cotizacion") || puedeVer("ventas.nueva_corporativa") ||
  puedeVer("ventas.nueva_manual") || puedeVer("ventas.historial");

const veCrm = () =>
  tieneModulo("crm") &&
  (puedeVer("contacts.view") || puedeVer("companies.view") || puedeVer("pipeline.view"));

const veTurismo = () =>
  tieneModulo("crm") && (puedeVer("bookings.view") || puedeVer("providers.view"));

const veOperaciones = () =>
  (tieneModulo("communication") && puedeVer("messaging.view")) ||
  (tieneModulo("crm") && (puedeVer("agenda.view") || puedeVer("tasks.view")));

const getRoute = () => {
  const route = useRoute();
  const routeArr = route.path.split("/");
  return routeArr[1];
};

const toggleMenu = (menu) => {
  openMenu.value = openMenu.value === menu ? null : menu;
};

const router = useRouter();

const logout = () => {
  localStorage.removeItem("accessToken");
  router.push("/signin");
};

// Funciones de IA que todavía no están construidas -- se muestran ya en el
// menú (a pedido del usuario) para dejar visible el rumbo, pero
// deshabilitadas hasta que se implementen una por una.
const IA_PROXIMAMENTE = [
  { text: "Generar respuesta", icon: "bi-chat-square-text" },
  { text: "Crear campaña", icon: "bi-megaphone" },
  { text: "Generar PDF", icon: "bi-file-earmark-pdf" },
  { text: "Analizar cliente", icon: "bi-person-lines-fill" },
  { text: "Resumir conversación", icon: "bi-card-text" },
  { text: "Crear itinerario", icon: "bi-map" },
  { text: "Asistente IA", icon: "bi-robot" },
];

// Badges de actividad: se refrescan cada 30s (mismo estilo de polling simple
// que Inbox.vue usa cada 5s para mensajes). Cada conteo se pide por separado
// y con su propio catch, para que si uno falla no tumbe a los demás.
const badgeClientes = ref(0);
const badgeEmbudo = ref(0);
const badgeChats = ref(0);
const badgeTareas = ref(0);
let intervaloBadges = null;

function cargarBadges() {
  if (tieneModulo("crm")) {
    api.get("/crm/contacts/count").then(res => { badgeClientes.value = res.data.count; }).catch(() => {});
    api.get("/crm/deals/stats").then(res => { badgeEmbudo.value = res.data.stats.cantidadAbiertos; }).catch(() => {});
    api.get("/crm/tasks/count").then(res => { badgeTareas.value = res.data.count; }).catch(() => {});
  }
  if (tieneModulo("communication")) {
    api.get("/communication/conversations/pending-count").then(res => { badgeChats.value = res.data.count; }).catch(() => {});
  }
}

onMounted(() => {
  cargarBadges();
  intervaloBadges = setInterval(cargarBadges, 30000);
});

onUnmounted(() => {
  if (intervaloBadges) clearInterval(intervaloBadges);
});
</script>


<template>
  <div class="collapse navbar-collapse w-auto h-auto h-100" id="sidenav-collapse-main">
    <ul class="navbar-nav">

      <!-- INICIO -->
      <li class="nav-item">
        <sidenav-item to="/dashboard" :class="getRoute() === 'dashboard' ? 'active' : ''" navText="Inicio">
          <template v-slot:icon>
            <i class="bi bi-house-door text-primary text-sm opacity-10"></i>
          </template>
        </sidenav-item>
      </li>

      <!-- VENTAS -->
      <li v-if="veVentas()" class="mt-3 nav-item">
        <h6 class="text-xs ps-4 text-uppercase font-weight-bolder opacity-6 sidenav-title">
          Ventas
        </h6>
      </li>

      <li v-if="veVentas()" class="nav-item">
        <div class="nav-link d-flex justify-content-between align-items-center cursor-pointer"
          @click="toggleMenu('quotation')" style="margin-left: 17px;">
          <div class="d-flex align-items-center">
            <i class="bi bi-briefcase text-primary text-sm opacity-10 me-2"></i>
            <span class="nav-link-text ms-1">Cotizaciones</span>
          </div>
          <i class="bi" :class="openMenu === 'quotation' ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>

        <ul v-if="openMenu === 'quotation'" class="nav flex-column ms-4">

          <li class="nav-item" v-if="puedeVer('ventas.nueva_cotizacion')">
            <sidenav-item to="/quote/create" navText="Nueva Cotización">
              <template v-slot:icon>
                <i class="bi bi-file-earmark-plus text-primary text-sm"></i>
              </template>
            </sidenav-item>
          </li>

          <li class="nav-item" v-if="puedeVer('ventas.nueva_corporativa')">
            <sidenav-item to="/quote/corp" navText="Nueva Corporativa">
              <template v-slot:icon>
                <i class="bi bi-buildings text-primary text-sm"></i>
              </template>
            </sidenav-item>
          </li>

          <li class="nav-item" v-if="puedeVer('ventas.nueva_manual')">
            <sidenav-item to="/quote/manual" navText="Nueva Manual">
              <template v-slot:icon>
                <i class="bi bi-pencil-square text-primary text-sm"></i>
              </template>
            </sidenav-item>
          </li>

          <li v-if="puedeVer('ventas.historial')"><hr class="horizontal dark my-2"></li>

          <li class="nav-item" v-if="puedeVer('ventas.historial')">
            <sidenav-item to="/quote/list" navText="Cotizaciones">
              <template v-slot:icon>
                <i class="bi bi-list-ul text-primary text-sm"></i>
              </template>
            </sidenav-item>
          </li>

        </ul>
      </li>

      <!-- CRM -->
      <li v-if="veCrm()" class="mt-3 nav-item">
        <h6 class="text-xs ps-4 text-uppercase font-weight-bolder opacity-6 sidenav-title">
          CRM
        </h6>
      </li>

      <li v-if="tieneModulo('crm') && puedeVer('contacts.view')" class="nav-item">
        <sidenav-item to="/crm/contacts" :class="$route.path.startsWith('/crm/contacts') ? 'active' : ''"
          navText="Clientes" :badge="badgeClientes">
          <template v-slot:icon>
            <i class="bi bi-people text-primary text-sm opacity-10"></i>
          </template>
        </sidenav-item>
      </li>

      <li v-if="tieneModulo('crm') && puedeVer('companies.view')" class="nav-item">
        <sidenav-item to="/crm/companies" navText="Empresas">
          <template v-slot:icon>
            <i class="bi bi-building text-primary text-sm opacity-10"></i>
          </template>
        </sidenav-item>
      </li>

      <li v-if="tieneModulo('crm') && puedeVer('pipeline.view')" class="nav-item">
        <sidenav-item to="/crm/pipeline" navText="Embudo" :badge="badgeEmbudo">
          <template v-slot:icon>
            <i class="bi bi-funnel text-primary text-sm opacity-10"></i>
          </template>
        </sidenav-item>
      </li>

      <!-- TURISMO (reservas de clientes + proveedores) -->
      <li v-if="veTurismo()" class="mt-3 nav-item">
        <h6 class="text-xs ps-4 text-uppercase font-weight-bolder opacity-6 sidenav-title">
          Turismo
        </h6>
      </li>

      <li v-if="tieneModulo('crm') && puedeVer('bookings.view')" class="nav-item">
        <sidenav-item to="/operations/bookings" navText="Reservas">
          <template v-slot:icon>
            <i class="bi bi-journal-check text-primary text-sm opacity-10"></i>
          </template>
        </sidenav-item>
      </li>

      <li v-if="tieneModulo('crm') && puedeVer('providers.view')" class="nav-item">
        <sidenav-item to="/operations/providers" navText="Proveedores">
          <template v-slot:icon>
            <i class="bi bi-truck text-primary text-sm opacity-10"></i>
          </template>
        </sidenav-item>
      </li>

      <!-- OPERACIONES -->
      <li v-if="veOperaciones()" class="mt-3 nav-item">
        <h6 class="text-xs ps-4 text-uppercase font-weight-bolder opacity-6 sidenav-title">
          Operaciones
        </h6>
      </li>

      <li v-if="tieneModulo('communication') && puedeVer('messaging.view')" class="nav-item">
        <sidenav-item to="/crm/inbox" navText="Chats" :badge="badgeChats">
          <template v-slot:icon>
            <i class="bi bi-chat-dots text-primary text-sm opacity-10"></i>
          </template>
        </sidenav-item>
      </li>

      <li v-if="tieneModulo('crm') && puedeVer('agenda.view')" class="nav-item">
        <sidenav-item to="/crm/agenda" navText="Agenda">
          <template v-slot:icon>
            <i class="bi bi-calendar-week text-primary text-sm opacity-10"></i>
          </template>
        </sidenav-item>
      </li>

      <li v-if="tieneModulo('crm') && puedeVer('tasks.view')" class="nav-item">
        <sidenav-item to="/crm/tasks" navText="Tareas" :badge="badgeTareas">
          <template v-slot:icon>
            <i class="bi bi-check2-square text-primary text-sm opacity-10"></i>
          </template>
        </sidenav-item>
      </li>

      <!-- ANALÍTICA -->
      <li v-if="veAnalitica()" class="mt-3 nav-item">
        <h6 class="text-xs ps-4 text-uppercase font-weight-bolder opacity-6 sidenav-title">
          Analítica
        </h6>
      </li>

      <li v-if="veAnalitica()" class="nav-item">
        <sidenav-item to="/billing" :class="$route.path === '/billing' ? 'active' : ''" navText="Reportes">
          <template v-slot:icon>
            <i class="bi bi-bar-chart-line text-primary text-sm opacity-10"></i>
          </template>
        </sidenav-item>
      </li>

      <li v-if="veAnalitica()" class="nav-item">
        <sidenav-item to="/operations/accounting" navText="Contabilidad">
          <template v-slot:icon>
            <i class="bi bi-calculator text-primary text-sm opacity-10"></i>
          </template>
        </sidenav-item>
      </li>

      <!-- ADMINISTRACIÓN -->
      <li v-if="user?.role === 'admin'" class="mt-3 nav-item">
        <h6 class="text-xs ps-4 text-uppercase font-weight-bolder opacity-6 sidenav-title">
          Administración
        </h6>
      </li>

      <li v-if="user?.role === 'admin'" class="nav-item">
        <sidenav-item to="/users" :class="getRoute() === 'users' ? 'active' : ''" navText="Usuarios">
          <template v-slot:icon>
            <i class="bi bi-person-gear text-primary text-sm opacity-10"></i>
          </template>
        </sidenav-item>
      </li>

      <li v-if="user?.role === 'admin' && tieneModulo('communication')" class="nav-item">
        <sidenav-item to="/crm/settings" navText="Conexiones">
          <template v-slot:icon>
            <i class="bi bi-plug text-primary text-sm opacity-10"></i>
          </template>
        </sidenav-item>
      </li>

      <li v-if="user?.role === 'admin'" class="nav-item">
        <sidenav-item to="/operations/invoice-settings" navText="Facturación">
          <template v-slot:icon>
            <i class="bi bi-receipt text-primary text-sm opacity-10"></i>
          </template>
        </sidenav-item>
      </li>

      <li v-if="user?.role === 'admin'" class="nav-item">
        <sidenav-item to="/parametros-cotizador" :class="getRoute() === 'parametros-cotizador' ? 'active' : ''"
          navText="Cotizador">
          <template v-slot:icon>
            <i class="bi bi-sliders text-primary text-sm opacity-10"></i>
          </template>
        </sidenav-item>
      </li>

      <li v-if="user?.role === 'admin' && tieneModulo('ai_lab')" class="nav-item">
        <div class="nav-link d-flex justify-content-between align-items-center cursor-pointer"
          @click="toggleMenu('ia')" style="margin-left: 17px;">
          <div class="d-flex align-items-center">
            <i class="bi bi-stars text-primary text-sm opacity-10 me-2"></i>
            <span class="nav-link-text ms-1">IA</span>
          </div>
          <i class="bi" :class="openMenu === 'ia' ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>

        <ul v-if="openMenu === 'ia'" class="nav flex-column ms-4">

          <li class="nav-item">
            <sidenav-item to="/crm/lab" navText="Laboratorio">
              <template v-slot:icon>
                <i class="bi bi-flask text-primary text-sm"></i>
              </template>
            </sidenav-item>
          </li>

          <li v-for="stub in IA_PROXIMAMENTE" :key="stub.text" class="nav-item">
            <div class="nav-link disabled d-flex align-items-center opacity-50" style="cursor: not-allowed;">
              <div class="icon icon-shape icon-sm text-center d-flex align-items-center justify-content-center">
                <i class="bi text-primary text-sm" :class="stub.icon"></i>
              </div>
              <span class="nav-link-text ms-1">{{ stub.text }}</span>
              <span class="badge bg-gradient-secondary rounded-pill ms-auto" style="font-size: 0.6rem;">Pronto</span>
            </div>
          </li>

        </ul>
      </li>

      <!-- PLATAFORMA (dueño del SaaS, sin cambios) -->
      <li v-if="user?.role === 'platform_admin'" class="mt-3 nav-item">
        <h6 class="text-xs ps-4 text-uppercase font-weight-bolder opacity-6 sidenav-title">
          Plataforma
        </h6>
      </li>

      <li v-if="user?.role === 'platform_admin'" class="nav-item">
        <sidenav-item to="/platform/companies" :class="getRoute() === 'platform' ? 'active' : ''" navText="Empresas">
          <template v-slot:icon>
            <i class="bi bi-building text-primary text-sm opacity-10"></i>
          </template>
        </sidenav-item>
      </li>

      <!-- CUENTA -->
      <li class="mt-3 nav-item">
        <h6 class="text-xs ps-4 text-uppercase font-weight-bolder opacity-6 sidenav-title">
          Mi Cuenta
        </h6>
      </li>

      <li class="nav-item">
        <sidenav-item to="/perfil" :class="getRoute() === 'perfil' ? 'active' : ''" navText="Mi Perfil">
          <template v-slot:icon>
            <i class="bi bi-person-circle text-primary text-sm opacity-10"></i>
          </template>
        </sidenav-item>
      </li>

      <li class="nav-item">
        <sidenav-item to="/billing/subscription"
          :class="getRoute() === 'billing' && $route.path.includes('subscription') ? 'active' : ''"
          navText="Plan y Facturación">
          <template v-slot:icon>
            <i class="bi bi-credit-card text-primary text-sm opacity-10"></i>
          </template>
        </sidenav-item>
      </li>

      <li class="nav-item mt-3">
        <div class="nav-link d-flex align-items-center cursor-pointer text-danger" @click="logout" style="margin-left: 17px;">
          <i class="bi bi-box-arrow-right text-danger text-sm"></i>
          <span class="nav-link-text ms-2">Cerrar Sesión</span>
        </div>
      </li>

    </ul>
  </div>


</template>
<style scoped>
.sidenav-title {
  transition: opacity 0.2s ease;
}

/* Sidebar minimizado de Argon */
.g-sidenav-hidden .sidenav-title {
  opacity: 0;
  visibility: hidden;
  height: 0;
  overflow: hidden;
  margin: 0;
}

#sidenav-collapse-main {
  overflow-x: hidden;
}
</style>
